import type {Cell, PastedCell} from 'notebook/tables/constants/CellConstants';
import {type Table} from 'notebook/tables/constants/TableConstants';
import {type MultiSelectionBox} from 'notebook/tables/constants/TableSelectionConstants';

/**
 * Preserve destination selector assignments on pasted cells before writing them into the table.
 * Used for fill-in only entries that cannot change selectors.
 *
 * @param table - The destination table.
 * @param cells - The pasted cells, positioned with their top-left at (`minI`, `minJ`).
 * @param minI - The top row at which the cells will be pasted.
 * @param minJ - The left column at which the cells will be pasted.
 * @returns The cells carrying the destination cells' selector assignments.
 */
export function preserveDestinationSelectorIdsForPaste(
  table: Table,
  cells: Array<Array<PastedCell>>,
  minI: number,
  minJ: number
): Array<Array<PastedCell>> {
  return cells.map((row, i) =>
    row.map((pastedCell, j) => preserveDestinationSelectorId(table.table[minI + i]?.[minJ + j], pastedCell))
  );
}

/**
 * Preserve destination selector assignments within a table region after fill-down.
 * Used for fill-in only entries that cannot change selectors.
 *
 * @param originalTable - The table before fill-down.
 * @param newTable - The table after fill-down.
 * @param box - The region that was filled into.
 * @returns A copy of `newTable` with destination selector assignments preserved in `box`.
 */
export function preserveDestinationSelectorIdsForFillDown(
  originalTable: Table,
  newTable: Table,
  box: MultiSelectionBox
): Table {
  const {minI, maxI, minJ, maxJ} = box;
  const table = newTable.table.map((row, i) =>
    row.map((cell, j) => {
      if (i < minI || i > maxI || j < minJ || j > maxJ) {
        return cell;
      }
      return preserveDestinationSelectorId(originalTable.table[i]?.[j], cell);
    })
  );
  return {...newTable, table};
}

function preserveDestinationSelectorId(originalCell: Cell | undefined, resultCell: Cell | PastedCell): Cell {
  const {selectorId, selector, ...rest} = resultCell as Cell & {selector?: unknown};
  const destinationSelectorId = originalCell?.selectorId;
  if (destinationSelectorId != null) {
    return {...rest, selectorId: destinationSelectorId};
  }
  return rest;
}
